import { pluralize } from './string-utils.js';

export const SubRequestAttendanceImpactKind = {
  regularRemaining: 'regular_remaining',
  shortNoticeRemaining: 'short_notice_remaining',
  lastOverall: 'last_overall',
  lastShortNotice: 'last_short_notice',
  exceedsOverall: 'exceeds_overall',
  exceedsShortNotice: 'exceeds_short_notice',
} as const;

export type SubRequestAttendanceImpactKind =
  (typeof SubRequestAttendanceImpactKind)[keyof typeof SubRequestAttendanceImpactKind];

export type SubRequestAttendanceImpactMessagePart = {
  text: string;
  isBold?: boolean;
};

export type SubRequestAttendanceImpactAllowance = {
  totalSubRequestUsage: number;
  totalSubRequestAllowance: number;
  shortNoticeSubRequestUsage: number;
  shortNoticeSubRequestAllowance: number;
};

export type SubRequestAttendanceImpact = {
  kind: SubRequestAttendanceImpactKind;
  totalUsed: number;
  totalAllowance: number;
  shortNoticeUsed: number;
  shortNoticeAllowance: number;
  messageParts: SubRequestAttendanceImpactMessagePart[];
};

function remainingPhrase(count: number): string {
  return `${count} ${pluralize(count, 'sub request', 'sub requests')} remaining`;
}

function shortNoticeRemainingPhrase(count: number): string {
  return `${count} ${pluralize(count, 'short-notice request', 'short-notice requests')}`;
}

function buildMessageParts(
  kind: SubRequestAttendanceImpactKind,
  remainingTotal: number,
  remainingShortNotice: number,
): SubRequestAttendanceImpactMessagePart[] {
  switch (kind) {
    case SubRequestAttendanceImpactKind.regularRemaining:
      return [
        { text: 'After this request, you will have ' },
        { text: remainingPhrase(remainingTotal), isBold: true },
        { text: ' for this student group.' },
      ];
    case SubRequestAttendanceImpactKind.shortNoticeRemaining:
      return [
        { text: 'This is a short-notice request. After this request, you will have ' },
        { text: remainingPhrase(remainingTotal), isBold: true },
        { text: ', including ' },
        { text: shortNoticeRemainingPhrase(remainingShortNotice), isBold: true },
        { text: '.' },
      ];
    case SubRequestAttendanceImpactKind.lastOverall:
      return [{ text: 'This is your last sub request within the attendance limit for this student group.' }];
    case SubRequestAttendanceImpactKind.lastShortNotice:
      return [
        { text: 'This is your last short-notice request within the attendance limit for this student group. ' },
        { text: 'After this request, you will have ' },
        { text: remainingPhrase(remainingTotal), isBold: true },
        { text: ' for this student group.' },
      ];
    case SubRequestAttendanceImpactKind.exceedsOverall:
      return [
        {
          text: 'You have reached your sub request limit for this student group. Submitting this request will exceed your attendance limit and may trigger the Student Protection Plan.',
        },
      ];
    case SubRequestAttendanceImpactKind.exceedsShortNotice:
      return [
        {
          text: 'You have reached your short-notice request limit for this student group. Submitting this request will exceed your attendance limit and may trigger the Student Protection Plan.',
        },
      ];
  }
}

/**
 * Attendance impact copy for the Request a substitute confirmation modal.
 * Uses current V2 usage/allowance (same as My student groups) and projects +1 for this request.
 */
export function getSubRequestAttendanceImpact({
  allowance,
  isShortNotice,
}: {
  allowance: SubRequestAttendanceImpactAllowance;
  isShortNotice: boolean;
}): SubRequestAttendanceImpact {
  const { totalSubRequestUsage, totalSubRequestAllowance, shortNoticeSubRequestUsage, shortNoticeSubRequestAllowance } =
    allowance;

  const projectedTotalUsage = totalSubRequestUsage + 1;
  const projectedShortNoticeUsage = isShortNotice ? shortNoticeSubRequestUsage + 1 : shortNoticeSubRequestUsage;
  const remainingTotal = totalSubRequestAllowance - projectedTotalUsage;
  const remainingShortNotice = shortNoticeSubRequestAllowance - projectedShortNoticeUsage;

  let kind: SubRequestAttendanceImpactKind = SubRequestAttendanceImpactKind.regularRemaining;

  if (projectedTotalUsage > totalSubRequestAllowance) {
    kind = SubRequestAttendanceImpactKind.exceedsOverall;
  } else if (isShortNotice && projectedShortNoticeUsage > shortNoticeSubRequestAllowance) {
    kind = SubRequestAttendanceImpactKind.exceedsShortNotice;
  } else if (projectedTotalUsage === totalSubRequestAllowance) {
    kind = SubRequestAttendanceImpactKind.lastOverall;
  } else if (isShortNotice && projectedShortNoticeUsage === shortNoticeSubRequestAllowance) {
    kind = SubRequestAttendanceImpactKind.lastShortNotice;
  } else if (isShortNotice) {
    kind = SubRequestAttendanceImpactKind.shortNoticeRemaining;
  }

  return {
    kind,
    totalUsed: totalSubRequestUsage,
    totalAllowance: totalSubRequestAllowance,
    shortNoticeUsed: shortNoticeSubRequestUsage,
    shortNoticeAllowance: shortNoticeSubRequestAllowance,
    messageParts: buildMessageParts(kind, remainingTotal, remainingShortNotice),
  };
}

export function formatSubRequestUsageCount(used: number, allowance: number): string {
  return `${used} of ${allowance}`;
}
